import React, { useEffect } from 'react';

/**
 * 本机能力说明弹窗。
 *
 * 为什么要有它：设置页底部那排「语音播报 ✓ / 振动 ✗」，用户只看到一个红叉，
 * 既不知道是谁的问题（设备？网络？软件坏了？），也不知道接下来该干什么。
 * 之前的做法是在下面常驻一句「『振动』用不了是 iPhone 的系统限制……」——
 * 问题有两个：一是只讲了 4 种能力里的 1 种，二是无论用户关不关心都占着一行。
 *
 * 现在改成：点带 ✗ 的标签才展开，一次把「什么情况 / 为什么 / 怎么办」说全。
 *
 * 三种原因的措辞口径不同，不能混着说：
 *   · 系统限制（iPhone 的振动）→ 明确说这是手机不给这个能力，换写法、换同类软件都没用，
 *     责任不在用户也不在本软件；同时必须给替代方案，否则用户只会觉得"这软件是残废"。
 *   · 浏览器不支持（语音合成 / 发声 / 语音识别）→ 说清"换一个浏览器可能就好了"，
 *     因为这一条对用户来说是真的可解的。
 *   · 静默失败的反面：不管哪种，都要写清"现在还能怎么用"，而不是只解释为什么不行。
 */
const CAPABILITY_INFO = {
  speech: {
    icon: '🔊',
    title: '「语音播报」用不了',
    symptom: '到点提醒时，这台设备不会把药名念出来。',
    reason: '这台设备的浏览器没有提供「把文字读出声」的能力。多半是浏览器版本太旧，或被浏览器本身限制住了。',
    fix: '换成 Chrome 或系统自带浏览器的最新版再打开，通常就好了。在那之前，把「到点提醒方式」改成「铃声」，一样不会错过。',
  },
  sound: {
    icon: '🔔',
    title: '「提示铃声」用不了',
    symptom: '到点提醒时，这台设备不会响提示音。',
    reason: '这台设备的浏览器不允许网页播放声音。通常是浏览器版本太旧，也可能是处在无痕 / 隐私模式下。',
    fix: '换最新版浏览器、或退出无痕模式再打开。也可以把「到点提醒方式」改成「振动」，靠震感提醒。',
  },
  vibrate: {
    icon: '📳',
    title: '「振动」用不了',
    symptom: '到点提醒时，这台设备不会振动。',
    reason: '振动要有振动马达才行：iPhone 上的浏览器（包括 Safari）从来没有提供振动能力，这是苹果的系统限制；电脑浏览器一般也没有振动马达。换任何写法、换同类的其他软件都绕不过去，不是本软件的问题。',
    fix: '把「到点提醒方式」改成「铃声」或「语音」；或者用安卓手机打开这一页，「振动」就能正常用了。',
  },
  recognition: {
    icon: '🎤',
    title: '「语音录入」用不了',
    symptom: '添加药品时，不能用说话的方式录入。',
    reason: '这台设备的浏览器不支持「语音识别」。常见于 Firefox，以及版本较旧的 Safari。',
    fix: '换成 Chrome 或系统自带浏览器打开就能用。在那之前，直接用打字或拍照录入，效果一样。',
  },
};

export default function DeviceCapabilityModal({ capability, onClose }) {
  const info = CAPABILITY_INFO[capability];

  // 按 Esc 关闭。弹窗拦掉了后面的内容，不给键盘出口等于把人关在里面
  useEffect(() => {
    if (!info) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [info, onClose]);

  if (!info) return null;

  return (
    // 点遮罩关闭：老人手指不精细，很容易点到边缘，这时应该关掉而不是什么都不发生
    <div className="popup-overlay" onClick={onClose}>
      <div
        className="popup"
        role="dialog"
        aria-modal="true"
        aria-label={info.title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="popup-title">{info.icon} {info.title}</div>
        <div className="popup-body cap-body">
          <p className="cap-row">
            <b>什么情况</b>
            {info.symptom}
          </p>
          <p className="cap-row">
            <b>为什么</b>
            {info.reason}
          </p>
          <p className="cap-row">
            <b>怎么办</b>
            {info.fix}
          </p>
        </div>
        <div className="popup-actions">
          <button className="btn-primary popup-btn-main" onClick={onClose}>
            知道了
          </button>
        </div>
      </div>
    </div>
  );
}
